import React, { useState, useEffect } from "react";
import { useLang } from "../i18n";
import { Clock, CalendarClock, CalendarX2 } from "lucide-react";

const SECONDS_PER_DAY = 86400;

const split = (ms) => {
  const totalSec = Math.floor(Math.max(0, ms) / 1000);
  return {
    days: Math.floor(totalSec / SECONDS_PER_DAY),
    hours: Math.floor((totalSec % SECONDS_PER_DAY) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
  };
};

// opensAt / closesAt: Date objects or null. Phase: unscheduled | upcoming | open | closed.
export function useCountdown(opensAt, closesAt = null) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!opensAt) {
    return { ...split(0), phase: "unscheduled", open: false, scheduled: false, opensAt: null, closesAt: null };
  }
  const toOpen = opensAt.getTime() - now;
  if (toOpen > 0) {
    return { ...split(toOpen), phase: "upcoming", open: false, scheduled: true, opensAt, closesAt };
  }
  if (closesAt && now >= closesAt.getTime()) {
    return { ...split(0), phase: "closed", open: false, scheduled: true, opensAt, closesAt };
  }
  return { ...split(closesAt ? closesAt.getTime() - now : 0), phase: "open", open: true, scheduled: true, opensAt, closesAt };
}

const pad = (n) => String(n).padStart(2, "0");

export function NextSessionBanner({ date, label }) {
  const { t, lang } = useLang();
  if (!date || date.getTime() <= Date.now()) return null;
  const days = Math.ceil((date.getTime() - Date.now()) / 86400000);
  return (
    <div className="rounded-xl bg-sky-50 border border-sky-200 p-5 flex items-start gap-3" data-testid="next-session-banner">
      <CalendarClock className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-sky-900">
          {t("countdown.next.title")}{label ? ` — ${label}` : ""}
        </p>
        <p className="text-xs text-sky-800 mt-1" data-testid="next-session-date">
          {t("countdown.next.opens")} {date.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "long", timeStyle: "short" })}
          {" · "}{t("countdown.next.in")} {days} {t("countdown.days")}
        </p>
      </div>
    </div>
  );
}

export function CountdownBanner({ cd }) {
  const { t, lang } = useLang();
  const fmtDate = (d) => d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "long", timeStyle: "short" });

  if (cd.phase === "open") {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 flex items-start gap-3" data-testid="countdown-open">
        <Clock className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-800">{t("countdown.open")}</p>
          {cd.closesAt && (
            <p className="text-xs text-emerald-700 mt-1" data-testid="countdown-closes-on">
              {t("countdown.closesOn")} {fmtDate(cd.closesAt)} · {cd.days} {t("countdown.days")} {pad(cd.hours)} h {pad(cd.minutes)} min
            </p>
          )}
        </div>
      </div>
    );
  }

  if (cd.phase === "closed") {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-5 flex items-start gap-3" data-testid="countdown-closed">
        <CalendarX2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-red-800">{t("countdown.closed")}</p>
          <p className="text-xs text-red-700 mt-1">{t("countdown.closedOn")} {fmtDate(cd.closesAt)}</p>
        </div>
      </div>
    );
  }

  if (cd.phase === "unscheduled") {
    return (
      <div className="rounded-xl bg-slate-100 border border-slate-200 p-5 flex items-center gap-3" data-testid="countdown-unscheduled">
        <CalendarClock className="w-5 h-5 text-slate-500 flex-shrink-0" />
        <p className="text-sm font-semibold text-slate-600">{t("countdown.unscheduled")}</p>
      </div>
    );
  }

  const units = [
    { v: cd.days, label: t("countdown.days") },
    { v: cd.hours, label: t("countdown.hours") },
    { v: cd.minutes, label: t("countdown.minutes") },
    { v: cd.seconds, label: t("countdown.seconds") },
  ];

  return (
    <div className="rounded-xl bg-secondary text-white p-6" data-testid="countdown">
      <div className="flex items-center gap-2 justify-center text-sm font-semibold uppercase tracking-wider text-white/80">
        <CalendarClock className="w-4 h-4" /> {t("countdown.title")}
      </div>
      <div className="mt-4 grid grid-cols-4 gap-2 sm:gap-3 max-w-md mx-auto">
        {units.map((u, i) => (
          <div key={u.label} className="rounded-lg bg-white/10 border border-white/15 py-3 text-center" data-testid={`countdown-unit-${i}`}>
            <div className="font-display text-2xl sm:text-3xl font-extrabold tabular-nums">{pad(u.v)}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">{u.label}</div>
          </div>
        ))}
      </div>
      {cd.closesAt && (
        <p className="mt-4 text-center text-xs text-white/70" data-testid="countdown-period">
          {t("countdown.period")} : {fmtDate(cd.opensAt)} → {fmtDate(cd.closesAt)}
        </p>
      )}
    </div>
  );
}
